// Barra de navegación translúcida: el contenido se desplaza por debajo
import { Link, NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import Logo from './Logo';

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
}

const navLink = ({ isActive }: { isActive: boolean }) =>
  `px-3 h-8 inline-flex items-center rounded-full text-footnote font-medium transition-colors ${
    isActive ? 'text-ink bg-ink/[0.06]' : 'text-ink-2 hover:text-ink'
  }`;

export default function Header() {
  const { user, isAuthenticated } = useAuthStore();
  const scrolled = useScrolled();

  const levelProgress = user && !user.isGuest && user.level < 100
    ? Math.max(0, Math.min(1, (user.xp - user.currentLevelXp) / Math.max(1, user.nextLevelXp - user.currentLevelXp)))
    : 1;

  return (
    <header
      className="sticky top-0 z-50 transition-[box-shadow] duration-200"
      style={{
        background: 'var(--nav-bg)',
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        // Borde solo cuando hay contenido debajo (efecto de borde al desplazar)
        boxShadow: scrolled ? '0 1px 0 rgb(var(--line) / 0.8)' : '0 1px 0 transparent',
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2">
        <Link to="/" className="mr-3 rounded-lg" aria-label="Jugaly, inicio">
          <Logo />
        </Link>

        <nav className="flex items-center gap-0.5" aria-label="Principal">
          <NavLink to="/" end className={navLink}>Juegos</NavLink>
          <NavLink to="/ranking" className={navLink}>Ranking</NavLink>
        </nav>

        <div className="flex-1" />

        {isAuthenticated && user ? (
          <Link
            to="/profile"
            className="flex items-center gap-2.5 pl-1 pr-1 sm:pr-3 h-10 rounded-full hover:bg-ink/[0.05] transition-colors"
            aria-label={`Perfil de ${user.username}`}
          >
            <span className="relative w-8 h-8 rounded-full bg-accent text-accent-ink grid place-items-center text-footnote font-semibold">
              {user.username.charAt(0).toUpperCase()}
            </span>
            <span className="hidden sm:flex flex-col leading-none gap-1 min-w-0">
              <span className="text-footnote font-semibold text-ink truncate max-w-[9rem]">{user.username}</span>
              {user.isGuest ? (
                <span className="text-caption text-ink-2">Invitado</span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <span className="text-caption font-medium text-ink-2 tabular">Nivel {user.level}</span>
                  <span className="w-12 h-1 rounded-full bg-ink/10 overflow-hidden">
                    <span
                      className="block h-full rounded-full bg-accent origin-left transition-transform duration-500 ease-out"
                      style={{ transform: `scaleX(${levelProgress})` }}
                    />
                  </span>
                </span>
              )}
            </span>
          </Link>
        ) : (
          <div className="flex items-center gap-1">
            <Link to="/auth" className="btn-plain h-9 text-footnote">Entrar</Link>
            <Link to="/auth?tab=register" className="btn-primary h-9 px-4 text-footnote hidden sm:inline-flex">Crear cuenta</Link>
          </div>
        )}
      </div>
    </header>
  );
}
